import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Eye, Loader2, User, Mail, Calendar, CreditCard, Activity, MessageSquare, Check, FileText } from 'lucide-react';
import { Button } from '../../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminImpersonate() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/api/admin/impersonate/${userId}`, { credentials: 'include' })
      .then(r => r.json()).then(setData).catch(() => navigate('/admin/users'))
      .finally(() => setLoading(false));
  }, [userId, navigate]);

  const sortedTones = useMemo(() => {
    if (!data?.stats?.tone_distribution) return [];
    return Object.entries(data.stats.tone_distribution).sort(([, a], [, b]) => b - a);
  }, [data?.stats?.tone_distribution]);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-white/30 animate-spin" /></div>;
  if (!data) return null;

  const { user, events, tickets, consent, feedback, referrals, stats } = data;

  return (
    <div className="space-y-6" data-testid="impersonate-view">
      {/* Banner */}
      <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 flex items-center gap-3">
        <Eye className="w-5 h-5 text-amber-400" />
        <div>
          <p className="text-amber-400 font-medium text-sm">View-as-User Mode (Read Only)</p>
          <p className="text-white/50 text-xs">Viewing {user.name}'s dashboard as they see it. No actions can be taken.</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => navigate('/admin/users')}
          className="ml-auto border-amber-500/30 text-amber-400 text-xs">
          <ArrowLeft className="w-3 h-3 mr-1" /> Exit
        </Button>
      </div>

      {/* User Header */}
      <div className="flex items-center gap-4">
        <div className="w-14 h-14 rounded-full bg-gradient-to-br from-cyan-500 to-purple-500 flex items-center justify-center text-white text-xl font-bold">
          {user.name?.[0]?.toUpperCase() || 'U'}
        </div>
        <div>
          <h1 className="text-2xl font-bold text-white font-space">{user.name}</h1>
          <p className="text-white/40 text-sm">{user.email} &middot; {user.plan} plan &middot; {stats.credits} credits</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'PI Checks', value: stats.total_checks, icon: Activity },
          { label: 'Month Checks', value: stats.month_checks, icon: Calendar },
          { label: 'Accept Rate', value: `${stats.acceptance_rate}%`, icon: Check },
          { label: 'Top Tone', value: stats.top_tone, icon: MessageSquare },
        ].map(s => (
          <div key={s.label} className="bg-[#0E1830] border border-white/10 rounded-xl p-4">
            <s.icon className="w-4 h-4 text-cyan-400 mb-2" />
            <p className="text-white font-bold text-lg">{s.value}</p>
            <p className="text-white/40 text-xs">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Tone Distribution */}
      {sortedTones.length > 0 && (
        <div className="bg-[#0E1830] border border-white/10 rounded-xl p-6">
          <h3 className="text-white font-medium mb-4">Tone Distribution</h3>
          <div className="flex flex-wrap gap-2">
            {sortedTones.map(([tone, count]) => (
              <span key={tone} className="px-3 py-1.5 bg-white/5 border border-white/10 rounded-full text-sm text-white/70 capitalize">
                {tone}: {count}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Recent Activity */}
      <div className="bg-[#0E1830] border border-white/10 rounded-xl p-6">
        <h3 className="text-white font-medium mb-4">Recent Activity ({events?.length || 0})</h3>
        <div className="space-y-2 max-h-64 overflow-y-auto">
          {(events || []).map((ev) => (
            <div key={`${ev.action}-${ev.created_at}`} className="flex items-center justify-between p-2 bg-white/5 rounded-lg text-sm">
              <div className="flex items-center gap-3">
                <span className="text-white/70">{ev.action}</span>
                <span className="text-white/30 text-xs">{ev.tone || ''}</span>
              </div>
              <span className="text-white/30 text-xs">{ev.created_at ? new Date(ev.created_at).toLocaleString() : ''}</span>
            </div>
          ))}
          {(!events || events.length === 0) && <p className="text-white/30 text-sm">No activity</p>}
        </div>
      </div>

      {/* Consent + Feedback */}
      <div className="grid lg:grid-cols-2 gap-4">
        <div className="bg-[#0E1830] border border-white/10 rounded-xl p-6">
          <h3 className="text-white font-medium mb-3">Consent</h3>
          {consent ? (
            <div className="text-sm space-y-1">
              <p className="text-white/70">Signed: {new Date(consent.signed_at).toLocaleString()}</p>
              <p className="text-white/70">Version: {consent.consent_version}</p>
            </div>
          ) : <p className="text-white/30 text-sm">Not signed</p>}
        </div>
        <div className="bg-[#0E1830] border border-white/10 rounded-xl p-6">
          <h3 className="text-white font-medium mb-3">Feedback</h3>
          {feedback ? (
            <div className="text-sm space-y-1">
              <p className="text-white/70">Value Score: {feedback.value_score}/10</p>
              <p className="text-white/70">Would Recommend: {feedback.would_recommend}</p>
            </div>
          ) : <p className="text-white/30 text-sm">Not submitted</p>}
        </div>
      </div>

      {/* Tickets */}
      {tickets?.length > 0 && (
        <div className="bg-[#0E1830] border border-white/10 rounded-xl p-6">
          <h3 className="text-white font-medium mb-3">Support Tickets ({tickets.length})</h3>
          <div className="space-y-2">
            {tickets.map((t) => (
              <div key={t.id || t.subject} className="flex justify-between p-2 bg-white/5 rounded-lg text-sm">
                <span className="text-white">{t.subject}</span>
                <span className={`px-2 py-0.5 text-xs rounded-full ${t.status === 'resolved' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-blue-500/20 text-blue-400'}`}>{t.status}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
